import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';
import { Reveal } from './Reveal';

/**
 * FAQ — Premium editorial accordion with better visual hierarchy
 * Redesigned for maximum readability and luxury aesthetic
 */
export const FAQ_ITEMS = [
  {
    q: 'How long does artificial grass last?',
    a: 'All of our premium European-manufactured artificial lawns are designed for long-term performance and are backed by a 10-year product guarantee. With proper installation and care, many lawns can last well beyond this period.',
  },
  {
    q: 'Is artificial grass safe for children and pets?',
    a: 'Yes. Our artificial lawns are non-toxic, soft underfoot and ideal for family gardens, schools and play areas. We also offer specialist Pet Lawn products with enhanced drainage and antibacterial technology.',
  },
  {
    q: 'Does artificial grass drain properly?',
    a: 'Absolutely. Proper drainage starts beneath the surface. Our installation process includes carefully prepared free-draining foundations and sub-base materials designed to allow water to drain efficiently and prevent standing water.',
  },
  {
    q: 'Will weeds grow through artificial grass?',
    a: 'We install a high-quality geotextile weed membrane beneath every lawn to minimise weed growth. While airborne seeds can occasionally settle on the surface, weeds growing through the lawn are extremely rare.',
  },
  {
    q: 'What maintenance is required?',
    a: 'Artificial grass requires very little maintenance. There is no mowing, watering or feeding required. Occasional brushing and removing leaves or debris will help keep your lawn looking its best.',
  },
  {
    q: 'Can artificial grass be installed on uneven ground?',
    a: 'Yes. Our experienced installation teams can regrade and prepare the ground to create a smooth, level finish. Proper groundwork is one of the key reasons customers choose Guardian Artificial Lawns.',
  },
  {
    q: 'What areas do you cover?',
    a: 'We operate throughout London and the surrounding counties, providing professional artificial lawn installations for residential, commercial, educational and sports environments.',
  },
  {
    q: 'How long does installation take?',
    a: 'Most domestic garden installations are completed within 1–2 days, depending on access, size and site conditions. Larger commercial and educational projects may take longer.',
  },
  {
    q: 'What guarantees do you offer?',
    a: 'We provide a 10-year product guarantee and a 24-month installation and groundwork guarantee — giving you complete peace of mind that your lawn has been built correctly from the ground up.',
  },
  {
    q: 'Why choose Guardian Artificial Lawns?',
    a: "Unlike many artificial grass installers, Guardian Artificial Lawns is backed by extensive groundworks and civil engineering expertise through Guardian Builders Ltd. We focus on what's beneath the surface as much as the lawn itself, ensuring superior drainage, stability and long-term performance.",
  },
  {
    q: 'Will my artificial lawn fade?',
    a: 'No. Our premium products are UV-stabilised and designed to retain their colour and appearance for years, even in direct sunlight.',
  },
  {
    q: 'Do I need a site survey?',
    a: 'Yes. We offer a free site survey and quotation. This allows us to assess drainage, access and ground conditions before recommending the most suitable solution for your property.',
  },
  {
    q: 'Can you install artificial grass over concrete or tarmac?',
    a: 'Yes. In some situations we can install artificial grass over existing hard surfaces using specialist shock pads and preparation methods, as demonstrated on some of our school and playground projects.',
  },
  {
    q: 'How much does artificial grass cost?',
    a: 'Every project is different and depends on factors such as lawn size, access, ground conditions and the product selected. Contact us for a free no-obligation quotation tailored to your requirements.',
  },
];

export default function FAQ({ 
  items = FAQ_ITEMS, 
  eyebrow = 'Common Questions', 
  title = 'FAQs', 
  titleItalic = '',
  limit, 
  light = true  // Default to light now for better readability
}) {
  const [openIdx, setOpenIdx] = useState(0);
  const list = limit ? items.slice(0, limit) : items;

  return (
    <section
      data-testid="faq-section"
      className="relative py-20 md:py-32 px-6 md:px-12 bg-[#E7E9E4] overflow-hidden"
    >
      {/* Subtle ambient background glow + faint grain — keeps the section feeling premium without competing with the frosted cards */}
      <div className="absolute -top-32 right-1/4 w-[600px] h-[600px] rounded-full bg-[#6A8F3E]/6 blur-[160px] pointer-events-none" />
      <div className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] rounded-full bg-[#314F2B]/5 blur-[140px] pointer-events-none" />
      <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
      
      <div className="mx-auto relative" style={{ maxWidth: '1400px' }}>
        <div className="grid grid-cols-12 gap-8 md:gap-16">
          {/* Left Column - Header (Sticky on desktop) */}
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <div className="inline-flex items-center gap-2 mb-6">
                  <span className="w-2 h-2 rounded-full bg-[#6A8F3E]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#314F2B] font-bold">
                    {eyebrow}
                  </span>
                </div>
              </Reveal>
              
              <h2 className="font-serif-editorial text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#1A1A1A] mb-6">
                {title}
                {titleItalic ? (
                  <span className="block italic text-[#6A8F3E] mt-1">{titleItalic}</span>
                ) : null}
              </h2>

              <Reveal delay={0.2}>
                <p className="text-base md:text-lg text-[#1A1A1A]/65 leading-relaxed">
                  Quick answers to the most common questions about artificial lawns, installation and guarantees.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Right Column - FAQ Accordion */}
          <div className="col-span-12 lg:col-span-8">
            <div className="space-y-4">
              {list.map((item, i) => {
                const open = openIdx === i;

                return (
                  <Reveal key={item.q} delay={i * 0.05}>
                    <motion.div
                      initial={false}
                      animate={{ y: 0 }}
                      className="group rounded-2xl border transition-all duration-500 overflow-hidden"
                      style={{
                        backdropFilter: 'blur(22px) saturate(135%)',
                        WebkitBackdropFilter: 'blur(22px) saturate(135%)',
                        background: open
                          ? 'linear-gradient(135deg, rgba(255,255,255,0.92), rgba(231,233,228,0.78))'
                          : 'linear-gradient(135deg, rgba(255,255,255,0.78), rgba(231,233,228,0.55))',
                        borderColor: open ? 'rgba(106,143,62,0.35)' : 'rgba(26,26,26,0.08)',
                        boxShadow: open
                          ? '0 30px 70px -30px rgba(22,42,30,0.18), inset 0 1px 0 rgba(255,255,255,0.7)'
                          : '0 18px 40px -28px rgba(22,42,30,0.12), inset 0 1px 0 rgba(255,255,255,0.6)',
                      }}
                    >
                      <button
                        onClick={() => setOpenIdx(open ? -1 : i)}
                        data-testid={`faq-toggle-${i}`}
                        aria-expanded={open}
                        className="w-full text-left p-6 md:p-8"
                      >
                        <div className="flex items-start justify-between gap-6">
                          {/* Question Number + Text */}
                          <div className="flex-1">
                            <div className="flex items-start gap-4">
                              {/* Number Badge */}
                              <span
                                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all duration-500"
                                style={{
                                  backgroundColor: open ? 'rgba(106, 143, 62, 0.18)' : 'rgba(106, 143, 62, 0.08)',
                                  color: '#314F2B',
                                }}
                              >
                                {String(i + 1).padStart(2, '0')}
                              </span>

                              {/* Question Text */}
                              <h3 className={`font-serif-editorial text-xl md:text-2xl lg:text-[1.75rem] leading-snug tracking-tight transition-colors duration-300 ${open ? 'text-[#1A1A1A]' : 'text-[#1A1A1A] group-hover:text-[#314F2B]'}`}>
                                {item.q}
                              </h3>
                            </div>
                          </div>

                          {/* Toggle Icon */}
                          <motion.div
                            animate={{ rotate: open ? 180 : 0 }}
                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                            className="flex-shrink-0 w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-500"
                            style={{
                              borderColor: open ? '#6A8F3E' : 'rgba(26, 26, 26, 0.18)',
                              backgroundColor: open ? '#6A8F3E' : 'transparent',
                              color: open ? '#FFFFFF' : '#1A1A1A',
                            }}
                          >
                            {open ? (
                              <Minus size={18} strokeWidth={2.5} />
                            ) : (
                              <Plus size={18} strokeWidth={2.5} />
                            )}
                          </motion.div>
                        </div>

                        {/* Answer (Expandable) */}
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                              className="overflow-hidden"
                            >
                              <motion.p
                                initial={{ y: -10 }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                                className="mt-6 ml-12 text-base md:text-lg text-[#1A1A1A]/75 leading-relaxed max-w-2xl"
                              >
                                {item.a}
                              </motion.p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </button>
                    </motion.div>
                  </Reveal>
                );
              })}
            </div>

            {/* Optional bottom CTA */}
            <Reveal delay={0.3}>
              <div className="mt-12 p-8 rounded-2xl bg-white border border-[#1A1A1A]/8 shadow-sm">
                <p className="text-base text-[#1A1A1A]/75 leading-relaxed mb-4">
                  Still have questions? Our team is here to help with expert advice on artificial lawns, groundworks and installation.
                </p>
                <a 
                  href="/contact" 
                  className="inline-flex items-center gap-2 text-sm tracking-wide font-semibold text-[#6A8F3E] hover:text-[#314F2B] transition-colors group"
                >
                  <span>Get in touch</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="group-hover:translate-x-1 transition-transform">
                    <path d="M1 8h14M9 2l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
